/**
 * MetricRadar: one polygon over the metric axes, drawn on Recharts (the Wave C
 * catalog's Radar). Used for the contractor's five dossier metrics; every spoke
 * names itself through the tooltip and the axis carries its own label.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, seriesColor, surfaceClass } from "./shared";

/** One spoke: the metric's label and its value as the dossier publishes it. */
export interface MetricSpoke {
  readonly label: string;
  readonly value: number;
}

export function MetricRadar({
  ariaLabel,
  data,
}: {
  ariaLabel: string;
  data: readonly MetricSpoke[];
}): React.JSX.Element {
  const total = data.reduce((sum, spoke) => sum + spoke.value, 0);
  if (data.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  const rows = data.map((spoke) => ({ label: spoke.label, value: spoke.value }));
  return (
    <figure className="q-chart" data-testid="metric-radar">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <RadarChart
            data={rows}
            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <PolarGrid stroke="var(--q-rule)" />
            <PolarAngleAxis dataKey="label" tickLine={false} />
            <PolarRadiusAxis tickLine={false} axisLine={false} tick={false} />
            <Tooltip
              isAnimationActive={false}
              content={({ active: hover, payload }) => {
                const row = payload[0]?.payload as Record<string, unknown> | undefined;
                if (!hover || row === undefined) {
                  return null;
                }
                const label = typeof row.label === "string" ? row.label : "";
                const value = typeof row.value === "number" ? row.value : Number(row.value ?? 0);
                return (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{strings.charts.mark(label, value)}</p>
                  </div>
                );
              }}
            />
            <Radar
              dataKey="value"
              stroke={seriesColor(1)}
              fill={seriesColor(1)}
              fillOpacity={0.3}
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}
